import { SandboxStore } from '../store';

export interface SandboxReportPeriod {
  from: Date;
  to: Date;
  dayCount: number;
  key: string;
}

export interface SandboxDailyReportFact {
  date: Date;
  dateKey: string;
  sessions: number;
  clientSessions: number;
  guestSessions: number;
  uniqueVisitors: number;
  newVisitors: number;
  averageSessionSeconds: number;
  occupiedSeconds: number;
  tariffRevenue: number;
  goodsRevenue: number;
  serviceRevenue: number;
  totalRevenue: number;
  depositRevenue: number;
  cashRevenue: number;
  cardRevenue: number;
  onlineRevenue: number;
  bonusesAccrued: number;
  bonusesSpent: number;
  bonusRefunds: number;
  incomingCashOrders: number;
  outgoingCashOrders: number;
  soldGoods: number;
  bookedSessions: number;
  bookedSeconds: number;
  bookedRevenue: number;
}

export interface SandboxReportTotals {
  sessions: number;
  clientSessions: number;
  guestSessions: number;
  uniqueClients: number;
  newClients: number;
  repeatVisitors: number;
  averageSessionSeconds: number;
  occupiedSeconds: number;
  availableSeconds: number;
  occupationPercent: number;
  tariffRevenue: number;
  goodsRevenue: number;
  serviceRevenue: number;
  totalRevenue: number;
  depositRevenue: number;
  cashRevenue: number;
  cardRevenue: number;
  onlineRevenue: number;
  bonusesAccrued: number;
  bonusesSpent: number;
  bonusRefunds: number;
  incomingCashOrders: number;
  outgoingCashOrders: number;
  soldGoods: number;
  bookedSessions: number;
  bookedSeconds: number;
  bookedRevenue: number;
}

export interface SandboxReportModel {
  period: SandboxReportPeriod;
  hostCount: number;
  days: SandboxDailyReportFact[];
  totals: SandboxReportTotals;
}

const DAY_MS = 86_400_000;

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const sumBy = <T>(items: T[], selector: (item: T) => number): number =>
  items.reduce((sum, item) => sum + selector(item), 0);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const inputObject = (
  variables: Record<string, unknown>
): Record<string, unknown> =>
  (variables.input as Record<string, unknown> | undefined) ?? {};

const parseDate = (value: unknown): Date | null => {
  if (!value) return null;
  const normalized = String(value).trim().replace(' ', 'T');
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? new Date(timestamp) : null;
};

const utcDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );

export const formatIsoDay = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getUTCDate()).padStart(2, '0')}`;

export const formatVisitorsDay = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCDate()).padStart(
    2,
    '0'
  )}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

export const hashReportKey = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const createReportRandom = (key: string): (() => number) => {
  let seed = hashReportKey(key);
  return () => {
    seed += 0x6d2b79f5;
    let value = seed;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const randomBetween = (
  random: () => number,
  min: number,
  max: number
): number => min + (max - min) * random();

export const getReportPeriod = (
  variables: Record<string, unknown>,
  store: SandboxStore
): SandboxReportPeriod => {
  const input = inputObject(variables);
  const fallbackTo = utcDay(new Date(store.getAnchorMs()));
  const fallbackFrom = new Date(fallbackTo.getTime() - 6 * DAY_MS);
  let from = utcDay(parseDate(input.from ?? input.start) ?? fallbackFrom);
  let to = utcDay(parseDate(input.to ?? input.end) ?? fallbackTo);

  if (from.getTime() > to.getTime()) {
    [from, to] = [to, from];
  }

  const dayCount = Math.max(
    1,
    Math.floor((to.getTime() - from.getTime()) / DAY_MS) + 1
  );

  return {
    from,
    to,
    dayCount,
    key: `${formatIsoDay(from)}:${formatIsoDay(to)}`,
  };
};

const createDailyFact = (
  date: Date,
  hostCount: number
): SandboxDailyReportFact => {
  const dateKey = formatIsoDay(date);
  const random = createReportRandom(`sandbox-club-report:${dateKey}`);
  const weekday = date.getUTCDay();
  const weekdayFactor = [1.18, 0.86, 0.9, 0.94, 1, 1.13, 1.27][weekday];
  const demandFactor = weekdayFactor * randomBetween(random, 0.93, 1.08);
  const sessions = Math.max(18, Math.round(130 * demandFactor));
  const guestSessions = Math.max(
    2,
    Math.round(sessions * randomBetween(random, 0.045, 0.075))
  );
  const clientSessions = sessions - guestSessions;
  const uniqueVisitors = Math.max(
    8,
    Math.round(clientSessions * randomBetween(random, 0.45, 0.53))
  );
  const newVisitors = Math.max(
    1,
    Math.round(uniqueVisitors * randomBetween(random, 0.15, 0.23))
  );
  const averageSessionSeconds = Math.round(
    randomBetween(random, 2.18, 2.62) * 3600
  );
  const availableSeconds = hostCount * 24 * 3600;
  const occupiedSeconds = Math.min(
    Math.round(availableSeconds * 0.86),
    sessions * averageSessionSeconds
  );

  const tariffRevenue = roundMoney(sessions * randomBetween(random, 242, 278));
  const goodsRevenue = roundMoney(sessions * randomBetween(random, 72, 96));
  const serviceRevenue = roundMoney(sessions * randomBetween(random, 8, 20));
  const totalRevenue = roundMoney(
    tariffRevenue + goodsRevenue + serviceRevenue
  );
  const cashShare = randomBetween(random, 0.22, 0.31);
  const onlineShare = randomBetween(random, 0.16, 0.24);
  const cashRevenue = roundMoney(totalRevenue * cashShare);
  const onlineRevenue = roundMoney(totalRevenue * onlineShare);
  const cardRevenue = roundMoney(totalRevenue - cashRevenue - onlineRevenue);
  const depositRevenue = roundMoney(
    totalRevenue * randomBetween(random, 0.58, 0.72)
  );
  const bonusesAccrued = roundMoney(
    totalRevenue * randomBetween(random, 0.065, 0.095)
  );
  const bonusesSpent = roundMoney(
    bonusesAccrued * randomBetween(random, 0.48, 0.72)
  );
  const bonusRefunds = roundMoney(
    bonusesAccrued * randomBetween(random, 0.005, 0.025)
  );
  const bookedSessions = Math.max(
    1,
    Math.round(sessions * randomBetween(random, 0.035, 0.065))
  );
  const bookedSeconds = Math.round(
    bookedSessions * averageSessionSeconds * randomBetween(random, 1.1, 1.45)
  );
  const bookedRevenue = roundMoney(
    bookedSessions * randomBetween(random, 520, 760)
  );

  return {
    date,
    dateKey,
    sessions,
    clientSessions,
    guestSessions,
    uniqueVisitors,
    newVisitors,
    averageSessionSeconds,
    occupiedSeconds,
    tariffRevenue,
    goodsRevenue,
    serviceRevenue,
    totalRevenue,
    depositRevenue,
    cashRevenue,
    cardRevenue,
    onlineRevenue,
    bonusesAccrued,
    bonusesSpent,
    bonusRefunds,
    incomingCashOrders: roundMoney(
      totalRevenue * randomBetween(random, 0.012, 0.035)
    ),
    outgoingCashOrders: roundMoney(
      totalRevenue * randomBetween(random, 0.008, 0.026)
    ),
    soldGoods: Math.max(
      1,
      Math.round(goodsRevenue / randomBetween(random, 145, 190))
    ),
    bookedSessions,
    bookedSeconds,
    bookedRevenue,
  };
};

export const createSandboxReportModel = (
  variables: Record<string, unknown>,
  store: SandboxStore
): SandboxReportModel => {
  const period = getReportPeriod(variables, store);
  const hostCount = Math.max(1, store.getState().hosts.length);
  const days = Array.from({ length: period.dayCount }, (_, index) =>
    createDailyFact(new Date(period.from.getTime() + index * DAY_MS), hostCount)
  );
  const sessions = sumBy(days, (day) => day.sessions);
  const occupiedSeconds = sumBy(days, (day) => day.occupiedSeconds);
  const availableSeconds = hostCount * period.dayCount * 24 * 3600;
  const uniqueClients = Math.max(
    1,
    Math.round(sumBy(days, (day) => day.uniqueVisitors) * 0.78)
  );
  const newClients = Math.min(uniqueClients, Math.round(uniqueClients * 0.19));

  const totals: SandboxReportTotals = {
    sessions,
    clientSessions: sumBy(days, (day) => day.clientSessions),
    guestSessions: sumBy(days, (day) => day.guestSessions),
    uniqueClients,
    newClients,
    repeatVisitors: Math.round(uniqueClients * 0.54),
    averageSessionSeconds: sessions
      ? Math.round(occupiedSeconds / sessions)
      : 0,
    occupiedSeconds,
    availableSeconds,
    occupationPercent: roundMoney(
      clamp((occupiedSeconds / availableSeconds) * 100, 0, 100)
    ),
    tariffRevenue: roundMoney(sumBy(days, (day) => day.tariffRevenue)),
    goodsRevenue: roundMoney(sumBy(days, (day) => day.goodsRevenue)),
    serviceRevenue: roundMoney(sumBy(days, (day) => day.serviceRevenue)),
    totalRevenue: roundMoney(sumBy(days, (day) => day.totalRevenue)),
    depositRevenue: roundMoney(sumBy(days, (day) => day.depositRevenue)),
    cashRevenue: roundMoney(sumBy(days, (day) => day.cashRevenue)),
    cardRevenue: roundMoney(sumBy(days, (day) => day.cardRevenue)),
    onlineRevenue: roundMoney(sumBy(days, (day) => day.onlineRevenue)),
    bonusesAccrued: roundMoney(sumBy(days, (day) => day.bonusesAccrued)),
    bonusesSpent: roundMoney(sumBy(days, (day) => day.bonusesSpent)),
    bonusRefunds: roundMoney(sumBy(days, (day) => day.bonusRefunds)),
    incomingCashOrders: roundMoney(
      sumBy(days, (day) => day.incomingCashOrders)
    ),
    outgoingCashOrders: roundMoney(
      sumBy(days, (day) => day.outgoingCashOrders)
    ),
    soldGoods: sumBy(days, (day) => day.soldGoods),
    bookedSessions: sumBy(days, (day) => day.bookedSessions),
    bookedSeconds: sumBy(days, (day) => day.bookedSeconds),
    bookedRevenue: roundMoney(sumBy(days, (day) => day.bookedRevenue)),
  };

  return { period, hostCount, days, totals };
};

export const reportMoney = roundMoney;

export const splitReportValue = (
  total: number,
  weights: number[],
  decimals = 0
): number[] => {
  if (!weights.length) return [];
  const safeWeights = weights.map((weight) => Math.max(0, weight));
  const weightSum = safeWeights.reduce((sum, weight) => sum + weight, 0) || 1;
  const multiplier = 10 ** decimals;
  const totalUnits = Math.round(total * multiplier);
  let allocated = 0;

  return safeWeights.map((weight, index) => {
    const units =
      index === safeWeights.length - 1
        ? totalUnits - allocated
        : Math.round((totalUnits * weight) / weightSum);
    allocated += units;
    return units / multiplier;
  });
};
